import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Paper,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Twitter,
  Facebook,
  Instagram,
  VideoLibrary,
  Visibility,
  ThumbUp,
  Share,
  Comment,
  Schedule,
  CheckCircle,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { socialMediaApi } from '../../api/socialMediaApi';

interface AnalyticsData {
  overview: {
    totalPosts: number;
    totalEngagement: number;
    averageEngagementRate: number;
    totalReach: number;
    postsThisWeek: number;
    postsLastWeek: number;
    engagementChange: number;
    bestPerformingPlatform: string;
  };
  platformBreakdown: {
    platform: string;
    posts: number;
    engagement: number;
    reach: number;
    engagementRate: number;
  }[];
  contentTypePerformance: {
    contentType: string;
    posts: number;
    avgEngagement: number;
    avgReach: number;
  }[];
  engagementTimeline: {
    date: string;
    likes: number;
    shares: number;
    comments: number;
    views: number;
  }[];
  topPosts: {
    postId: string;
    platform: string;
    contentType: string;
    engagement: number;
    postedAt: string;
    content: string;
  }[];
  postingFrequency: {
    hour: number;
    posts: number;
    avgEngagement: number;
  }[];
}

const SocialAnalytics: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');
  const [platform, setPlatform] = useState('all');

  useEffect(() => {
    fetchAnalytics();
  }, [timeRange, platform]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await socialMediaApi.getAnalytics({
        timeRange,
        platform: platform === 'all' ? undefined : platform,
      });
      setAnalytics(response);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'twitter':
        return <Twitter sx={{ color: '#1DA1F2' }} />;
      case 'facebook':
        return <Facebook sx={{ color: '#1877F2' }} />;
      case 'instagram':
        return <Instagram sx={{ color: '#E4405F' }} />;
      case 'tiktok':
        return <VideoLibrary sx={{ color: '#000000' }} />;
      default:
        return null;
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toString();
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  if (loading || !analytics) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const changeIndicator = (change: number) => {
    if (change > 0) {
      return (
        <Chip
          label={`+${change}%`}
          color="success"
          size="small"
          icon={<TrendingUp />}
        />
      );
    } else if (change < 0) {
      return (
        <Chip
          label={`${change}%`}
          color="error"
          size="small"
          icon={<TrendingDown />}
        />
      );
    }
    return <Chip label="0%" size="small" />;
  };

  return (
    <Box>
      {/* Controls */}
      <Box display="flex" gap={2} mb={3}>
        <FormControl size="small">
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label="Time Range"
          >
            <MenuItem value="24h">Last 24 Hours</MenuItem>
            <MenuItem value="7d">Last 7 Days</MenuItem>
            <MenuItem value="30d">Last 30 Days</MenuItem>
            <MenuItem value="90d">Last 90 Days</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small">
          <InputLabel>Platform</InputLabel>
          <Select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            label="Platform"
          >
            <MenuItem value="all">All Platforms</MenuItem>
            <MenuItem value="twitter">Twitter</MenuItem>
            <MenuItem value="facebook">Facebook</MenuItem>
            <MenuItem value="instagram">Instagram</MenuItem>
            <MenuItem value="tiktok">TikTok</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Overview Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Posts
                  </Typography>
                  <Typography variant="h4">
                    {analytics.overview.totalPosts}
                  </Typography>
                </Box>
                <Schedule fontSize="large" color="primary" />
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  This week: {analytics.overview.postsThisWeek}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Engagement
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.overview.totalEngagement)}
                  </Typography>
                </Box>
                <ThumbUp fontSize="large" color="primary" />
              </Box>
              <Box mt={1}>
                {changeIndicator(analytics.overview.engagementChange)}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Avg. Engagement Rate
                  </Typography>
                  <Typography variant="h4">
                    {analytics.overview.averageEngagementRate.toFixed(1)}%
                  </Typography>
                </Box>
                <TrendingUp fontSize="large" color="success" />
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  Best: {analytics.overview.bestPerformingPlatform}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="text.secondary" gutterBottom>
                    Total Reach
                  </Typography>
                  <Typography variant="h4">
                    {formatNumber(analytics.overview.totalReach)}
                  </Typography>
                </Box>
                <Visibility fontSize="large" color="primary" />
              </Box>
              <Box mt={1}>
                <Typography variant="body2" color="text.secondary">
                  Unique viewers
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3}>
        {/* Engagement Timeline */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Engagement Timeline
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics.engagementTimeline}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="likes" stroke="#8884d8" name="Likes" />
                <Line type="monotone" dataKey="shares" stroke="#82ca9d" name="Shares" />
                <Line type="monotone" dataKey="comments" stroke="#ffc658" name="Comments" />
                <Line type="monotone" dataKey="views" stroke="#ff7c7c" name="Views" />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Platform Breakdown */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Platform Breakdown
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics.platformBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.platform}: ${entry.posts}`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="posts"
                >
                  {analytics.platformBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Content Type Performance */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Content Type Performance
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.contentTypePerformance}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="contentType" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="avgEngagement" fill="#8884d8" name="Avg Engagement" />
                <Bar dataKey="avgReach" fill="#82ca9d" name="Avg Reach" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Best Posting Times */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Best Posting Times
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics.postingFrequency}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="hour" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="avgEngagement" fill="#ffc658" name="Avg Engagement" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Platform Performance Table */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Platform Performance
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Box sx={{ minWidth: 650 }}>
                {analytics.platformBreakdown.map((platform) => (
                  <Box
                    key={platform.platform}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      py: 2,
                      borderBottom: '1px solid rgba(0,0,0,0.1)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', width: 150 }}>
                      {getPlatformIcon(platform.platform)}
                      <Typography sx={{ ml: 1 }}>{platform.platform}</Typography>
                    </Box>
                    <Box sx={{ flex: 1, mx: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Engagement Rate
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <LinearProgress
                          variant="determinate"
                          value={Math.min(platform.engagementRate * 10, 100)}
                          sx={{ flex: 1, mr: 2, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2">
                          {platform.engagementRate.toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: 'center', width: 100 }}>
                      <Typography variant="caption" color="text.secondary">
                        Posts
                      </Typography>
                      <Typography variant="h6">{platform.posts}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'center', width: 100 }}>
                      <Typography variant="caption" color="text.secondary">
                        Reach
                      </Typography>
                      <Typography variant="h6">{formatNumber(platform.reach)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Top Performing Posts */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Top Performing Posts
            </Typography>
            {analytics.topPosts.map((post) => (
              <Box
                key={post.postId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  py: 2,
                  borderBottom: '1px solid rgba(0,0,0,0.1)',
                }}
              >
                <Box sx={{ mr: 2 }}>{getPlatformIcon(post.platform)}</Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" noWrap sx={{ maxWidth: 400 }}>
                    {post.content}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {post.contentType} • {new Date(post.postedAt).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ThumbUp fontSize="small" sx={{ mr: 1 }} />
                  <Typography>{formatNumber(post.engagement)}</Typography>
                </Box>
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SocialAnalytics;